import Column from '@app/components/layout/Column'
import { Form } from '@app/components/core/form/Form'
import { Text } from '@app/components/core/typography/Text'
import { PLOT_STATUS, PlotStatus } from '@schema/initiative'
import { useTranslations } from '@i18n/useLanguage'
import { useCallback, useMemo } from 'react'
import { useClient } from '@based/sdk/react'
import { useToast } from '@app/util/hooks/useToast'
import { EditContainer } from './blocks/shared/EditContainer'
import { useEditContainerClose } from './blocks/shared/useEditContainerClose'
import { FieldEditActions } from './blocks/shared/FieldEditActions'
import { match } from 'ts-pattern'
import { useFormContext } from 'react-hook-form'
import { enrich } from '@i18n/localizeHelper'
import {
  createLegalInfoFormSchema,
  type LegalInfoFormValues,
} from '@app/pages/dashboard/initiative/shared/validation/initiativeValidation'
import {
  legalInfoDefaultValues,
  legalInfoToEditPayload,
} from '@app/pages/dashboard/initiative/shared/forms/legalInfo/legalInfoFormMappers'
import {
  LegalInfoFormFields,
  LegalInfoFormInitialSync,
} from '@app/pages/dashboard/initiative/shared/forms/legalInfo/LegalInfoFormFields'
import { InitiativeWithId } from '../../../shared/initiativeTypes'

export type LegalInfoEditProps = {
  initiative: InitiativeWithId
}

const LegalInfoAboutEditFormBody = ({
  initial,
}: {
  initial: LegalInfoFormValues
}) => {
  const close = useEditContainerClose()
  if (!close) {
    throw new Error(
      'LegalInfoAboutEditFormBody requires an EditContainer ancestor',
    )
  }

  const { reset, formState } = useFormContext<LegalInfoFormValues>()

  return (
    <Column gap="2.5rem" alignItems="stretch">
      <LegalInfoFormInitialSync initial={initial} />
      <LegalInfoFormFields />
      <FieldEditActions
        onCancel={() => {
          reset(initial)
          close()
        }}
        saveDisabled={!formState.isValid || formState.isSubmitting}
      />
    </Column>
  )
}

const plotStatusViewLabel = (
  status: PlotStatus | null | undefined,
  creation: {
    notUnderOptionButInMind: string
  },
  common: { yes: string; no: string },
) =>
  match(status ?? PLOT_STATUS.noPlot)
    .with(PLOT_STATUS.underOption, () => common.yes)
    .with(PLOT_STATUS.noPlot, () => common.no)
    .with(
      PLOT_STATUS.potentialPlotInMind,
      () => creation.notUnderOptionButInMind,
    )
    .exhaustive()

const LegalInfoView = ({ initiative }: LegalInfoEditProps) => {
  const translationsRoot = useTranslations().app
  const common = translationsRoot.common
  const creation = translationsRoot.dashboard.initiative.creation
  const emptyLabel = translationsRoot.common.notSpecified

  const status = initiative.potentialPlotStatus ?? PLOT_STATUS.noPlot
  const plotQuestions = status !== PLOT_STATUS.noPlot

  return (
    <Column gap="1rem" alignItems="stretch">
      <Text.Medium color="var(--palette-blue-500)">
        {creation.plotUnderOption}:{' '}
        {plotStatusViewLabel(initiative.potentialPlotStatus, creation, common)}
      </Text.Medium>
      {plotQuestions && (
        <Text.Medium color="var(--palette-blue-500)">
          {creation.address}:{' '}
          {(initiative.potentialPlotAddress ?? '').trim() !== ''
            ? initiative.potentialPlotAddress
            : emptyLabel}
        </Text.Medium>
      )}
      {plotQuestions && (
        <YesNoField label={creation.permit} value={initiative.permit} />
      )}
      <YesNoField
        label={creation.financingRequirementsConstruction}
        value={initiative.financingRequirementsConstruction}
      />
      <YesNoField
        label={creation.financingRequirementsPlanning}
        value={initiative.financingRequirementsPlanning}
      />
      <YesNoField
        label={creation.kvkRegistration}
        value={initiative.kvkRegistration}
      />
    </Column>
  )
}

const LegalInfoFormInner = ({ initiative }: LegalInfoEditProps) => {
  const translationsRoot = useTranslations().app
  const formValidation = translationsRoot.form.validation
  const common = translationsRoot.common
  const client = useClient()
  const { showFailToast, showSuccessToast } = useToast()
  const close = useEditContainerClose()
  if (!close) {
    throw new Error('LegalInfoFormInner requires an EditContainer ancestor')
  }

  const {
    potentialPlotStatus,
    potentialPlotAddress,
    permit,
    financingRequirementsConstruction,
    financingRequirementsPlanning,
    kvkRegistration,
    id: initiativeId,
  } = initiative

  const schema = useMemo(
    () => createLegalInfoFormSchema(enrich(formValidation.minLength, 6)),
    [formValidation.minLength],
  )

  const initialValues = useMemo(
    () =>
      legalInfoDefaultValues({
        potentialPlotStatus,
        potentialPlotAddress,
        permit,
        financingRequirementsConstruction,
        financingRequirementsPlanning,
        kvkRegistration,
      }),
    [
      potentialPlotStatus,
      potentialPlotAddress,
      permit,
      financingRequirementsConstruction,
      financingRequirementsPlanning,
      kvkRegistration,
    ],
  )

  const onSubmit = useCallback(
    (data: LegalInfoFormValues) => {
      if (!initiativeId) {
        return
      }
      void client
        .call('edit-initiative', {
          id: initiativeId,
          value: legalInfoToEditPayload(data),
        })
        .then((success) => {
          if (success) {
            showSuccessToast(common.toastSuccess)
            close()
          } else {
            showFailToast()
          }
        })
        .catch(() => showFailToast())
    },
    [
      client,
      initiativeId,
      close,
      showFailToast,
      showSuccessToast,
      common.toastSuccess,
    ],
  )

  return (
    <Form<LegalInfoFormValues>
      schema={schema}
      defaultValues={initialValues}
      mode="onChange"
      onSubmit={onSubmit}
    >
      <LegalInfoAboutEditFormBody initial={initialValues} />
    </Form>
  )
}

export const LegalInfoEdit = ({ initiative }: LegalInfoEditProps) => {
  const view = useTranslations().app.dashboard.initiative.view

  return (
    <EditContainer title={view.legalInfoSection}>
      <EditContainer.View>
        <LegalInfoView initiative={initiative} />
      </EditContainer.View>
      <EditContainer.Edit>
        <LegalInfoFormInner initiative={initiative} />
      </EditContainer.Edit>
    </EditContainer>
  )
}

const YesNoField = ({ label, value }: { label: string; value: boolean }) => {
  const t = useTranslations().app.common
  return (
    <Text.Medium color="var(--palette-blue-500)">
      {label}:{' '}
      {match(value)
        .with(true, () => t.yes)
        .with(false, () => t.no)
        .otherwise(() => t.notSpecified)}
    </Text.Medium>
  )
}
